import {
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Link,
} from "@mui/material";
import React, { useEffect } from 'react';
import logAnalyticsEvent from '../../services/AnalyticsManager/AnalyticsManager';
import { GGAnalyticsEvents } from '../../services/AnalyticsManager/GGAnalyticsEvents';

export default function PrivacyPolicy() {
  useEffect(() => {
    document.title = "GenieGig | Privacy Policy";
    logAnalyticsEvent(GGAnalyticsEvents.PRIVACY_POLICY_PAGE_LOADED);
  }, []);

  const sections = [
    "1. Information We Collect",
    "2. How We Use Your Information",
    "3. Information Sharing and Disclosure",
    "4. Data Security and Protection",
    "5. Your Rights and Choices",
    "6. Cookies and Tracking Technologies",
    "7. Data Retention",
    "8. International Data Transfers",
    "9. Children's Privacy",
    "10. Third-Party Services",
    "11. Legal Basis for Processing",
    "12. Changes to This Privacy Policy",
    "13. Contact Us",
  ];

  return (
    <Box maxWidth="md" mx="auto" p={4} color="text.primary">
      <div
        style={{ display: "flex", alignItems: "center", marginBottom: "20px" }}
      >
        <Typography
          variant="h4"
          gutterBottom
          fontFamily="Goodfish"
          style={{ fontWeight: 700, marginRight: "10px" }}
        >
          © GenieGig
        </Typography>
        <Typography variant="h4" gutterBottom>
          Privacy Policy
        </Typography>
      </div>

      <Box mb={3}>
        <Typography variant="body1" color="text.secondary" style={{ fontStyle: 'italic' }}>
          Last Updated: 23 October 2025
        </Typography>
      </Box>
      
      <Box mb={4}>
        <Typography variant="h6" gutterBottom>
          Contents
        </Typography>
        <List>
          {sections.map((title, index) => (
            <ListItem key={index} disablePadding>
              <Link
                href={`#section-${index + 1}`}
                underline="hover"
                color="blue"
              >
                <ListItemText primary={title} />
              </Link>
            </ListItem>
          ))}
        </List>
      </Box>

      <Typography id="section-1" variant="h5" gutterBottom mt={4}>
        1. Information We Collect
      </Typography>
      <Typography variant="h6" gutterBottom>
        Personal Information
      </Typography>
      <List>
        <ListItem>
          <ListItemText primary="Account Information: Name, email address, password, phone number, and location." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Profile Information: Profile picture, bio, skills, portfolio, and verification documents." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Identity Verification: Government-issued ID, business registration documents, and tax information." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Payment Information: Bank account details, payment method information, and transaction history." />
        </ListItem>
      </List>
      
      <Typography variant="h6" gutterBottom>
        Service-Related Information
      </Typography>
      <List>
        <ListItem>
          <ListItemText primary="Project Information: Gig descriptions, requirements, budgets, deadlines, and deliverables." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Communication Data: Messages, feedback, reviews, and support communications." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Order Information: Order details, delivery information, and dispute records." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Content: Files, images, videos, and other materials uploaded to our platform." />
        </ListItem>
      </List>

      <Typography variant="h6" gutterBottom>
        Technical Information
      </Typography>
      <List>
        <ListItem>
          <ListItemText primary="Device Information: IP address, browser type, operating system, and device identifiers." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Usage Data: Pages visited, features used, time spent, and interaction patterns." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Location Data: Geographic location based on IP address or GPS (with consent)." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Cookies and Tracking: Information collected through cookies, web beacons, and similar technologies." />
        </ListItem>
      </List>

      <Typography id="section-2" variant="h5" gutterBottom mt={4}>
        2. How We Use Your Information
      </Typography>
      <Typography variant="h6" gutterBottom>
        Service Provision
      </Typography>
      <List>
        <ListItem>
          <ListItemText primary="Provide and maintain our platform services, including gig creation, order management, and payment processing." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Facilitate communication between clients and freelancers through our messaging system." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Process payments, handle escrow transactions, and manage financial operations." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Verify user identities and maintain platform security and integrity." />
        </ListItem>
      </List>

      <Typography variant="h6" gutterBottom>
        Platform Improvement
      </Typography>
      <List>
        <ListItem>
          <ListItemText primary="Analyze usage patterns to improve platform functionality and user experience." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Develop new features and services based on user needs and feedback." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Conduct research and analytics to understand market trends and user behavior." />
        </ListItem>
      </List>

      <Typography variant="h6" gutterBottom>
        Communication and Marketing
      </Typography>
      <List>
        <ListItem>
          <ListItemText primary="Send service-related notifications, updates, and important announcements." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Provide customer support and respond to inquiries and complaints." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Send promotional content and marketing communications (with your consent)." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Conduct surveys and gather feedback to improve our services." />
        </ListItem>
      </List>

      <Typography variant="h6" gutterBottom>
        Legal and Compliance
      </Typography>
      <List>
        <ListItem>
          <ListItemText primary="Enforce our Terms of Service and other platform policies." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Comply with legal obligations and respond to lawful requests from authorities." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Protect against fraud, abuse, and other illegal activities." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Resolve disputes and handle legal proceedings." />
        </ListItem>
      </List>

      <Typography id="section-3" variant="h5" gutterBottom mt={4}>
        3. Information Sharing and Disclosure
      </Typography>
      <Typography variant="h6" gutterBottom>
        Service Providers and Partners
      </Typography>
      <List>
        <ListItem>
          <ListItemText primary="Payment Processors: Share payment information with trusted payment service providers to process transactions." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Cloud Services: Use cloud service providers for data storage, processing, and platform infrastructure." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Analytics Providers: Share usage data with analytics services to improve platform performance." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Communication Services: Use third-party services for email delivery and customer support." />
        </ListItem>
      </List>

      <Typography variant="h6" gutterBottom>
        Legal Requirements
      </Typography>
      <List>
        <ListItem>
          <ListItemText primary="Law Enforcement: Disclose information when required by law, court order, or legal process." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Government Requests: Respond to lawful requests from government authorities and regulatory bodies." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Legal Proceedings: Share information in connection with legal proceedings, investigations, or disputes." />
        </ListItem>
      </List>

      <Typography variant="h6" gutterBottom>
        Business Transfers
      </Typography>
      <List>
        <ListItem>
          <ListItemText primary="Mergers and Acquisitions: Transfer information in connection with business mergers, acquisitions, or asset sales." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Corporate Restructuring: Share data during corporate reorganizations or restructuring activities." />
        </ListItem>
      </List>

      <Typography variant="h6" gutterBottom>
        User Consent
      </Typography>
      <List>
        <ListItem>
          <ListItemText primary="Explicit Consent: Share information with third parties when you have given explicit consent." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Public Information: Display publicly available profile information and gig listings as intended." />
        </ListItem>
      </List>

      <Typography id="section-4" variant="h5" gutterBottom mt={4}>
        4. Data Security and Protection
      </Typography>
      <Typography variant="h6" gutterBottom>
        Security Measures
      </Typography>
      <List>
        <ListItem>
          <ListItemText primary="Encryption: We use industry-standard encryption to protect data in transit and at rest." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Access Controls: Implement strict access controls and authentication mechanisms." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Regular Audits: Conduct regular security audits and vulnerability assessments." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Employee Training: Provide security training to employees handling personal data." />
        </ListItem>
      </List>

      <Typography variant="h6" gutterBottom>
        Data Breach Response
      </Typography>
      <List>
        <ListItem>
          <ListItemText primary="Incident Response: Maintain procedures for detecting, investigating, and responding to data breaches." />
        </ListItem>
        <ListItem>
          <ListItemText primary="User Notification: Notify affected users promptly in case of a data breach that poses a risk to their rights." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Regulatory Reporting: Report breaches to relevant authorities as required by law." />
        </ListItem>
      </List>

      <Typography paragraph>
        While we implement reasonable security measures, no method of transmission over the internet or electronic storage is 100% secure. We cannot guarantee absolute security of your information.
      </Typography>

      <Typography id="section-5" variant="h5" gutterBottom mt={4}>
        5. Your Rights and Choices
      </Typography>
      <Typography variant="h6" gutterBottom>
        Access and Control
      </Typography>
      <List>
        <ListItem>
          <ListItemText primary="Access: Request access to your personal information and receive a copy of the data we hold about you." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Correction: Update or correct inaccurate or incomplete personal information." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Deletion: Request deletion of your personal information, subject to legal and operational requirements." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Portability: Receive your data in a structured, machine-readable format for transfer to another service." />
        </ListItem>
      </List>

      <Typography variant="h6" gutterBottom>
        Communication Preferences
      </Typography>
      <List>
        <ListItem>
          <ListItemText primary="Email Preferences: Opt out of promotional emails while maintaining essential service communications." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Push Notifications: Control push notification settings through your device or account settings." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Marketing Communications: Unsubscribe from marketing communications at any time." />
        </ListItem>
      </List>

      <Typography variant="h6" gutterBottom>
        Account Management
      </Typography>
      <List>
        <ListItem>
          <ListItemText primary="Profile Settings: Update your profile information, privacy settings, and account preferences." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Account Deactivation: Deactivate or delete your account, subject to our retention policies." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Data Export: Request a copy of your data for personal use or migration purposes." />
        </ListItem>
      </List>

      <Typography paragraph>
        To exercise these rights, please contact us using the information provided in the Contact Us section. We will respond to your request within a reasonable timeframe as required by applicable law.
      </Typography>

      <Typography id="section-6" variant="h5" gutterBottom mt={4}>
        6. Cookies and Tracking Technologies
      </Typography>
      <Typography variant="h6" gutterBottom>
        Types of Cookies
      </Typography>
      <List>
        <ListItem>
          <ListItemText primary="Essential Cookies: Necessary for platform functionality, security, and basic features." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Performance Cookies: Collect information about how you use our platform to improve performance." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Functional Cookies: Remember your preferences and settings for a better user experience." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Marketing Cookies: Used to deliver relevant advertisements and track campaign effectiveness." />
        </ListItem>
      </List>

      <Typography variant="h6" gutterBottom>
        Third-Party Tracking
      </Typography>
      <List>
        <ListItem>
          <ListItemText primary="Analytics Services: Use Google Analytics and similar services to understand user behavior." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Social Media: Integration with social media platforms for sharing and authentication." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Advertising Networks: Partner with advertising networks to display relevant ads." />
        </ListItem>
      </List>

      <Typography paragraph>
        You can control cookie settings through your browser preferences. However, disabling certain cookies may affect platform functionality. For more information about our cookie practices, please refer to our Cookie Policy.
      </Typography>

      <Typography id="section-7" variant="h5" gutterBottom mt={4}>
        7. Data Retention
      </Typography>
      <Typography variant="h6" gutterBottom>
        Retention Periods
      </Typography>
      <List>
        <ListItem>
          <ListItemText primary="Account Information: Retained for the duration of your account plus 3 years for legal and business purposes." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Transaction Records: Kept for 7 years to comply with financial regulations and tax requirements." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Communication Data: Retained for 2 years after the last interaction for customer support purposes." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Usage Analytics: Aggregated and anonymized data may be retained indefinitely for research purposes." />
        </ListItem>
      </List>

      <Typography variant="h6" gutterBottom>
        Deletion Process
      </Typography>
      <List>
        <ListItem>
          <ListItemText primary="Automatic Deletion: Some data is automatically deleted after specified retention periods." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Manual Deletion: You can request deletion of specific data, subject to legal and operational requirements." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Account Closure: Upon account closure, most personal data is deleted within 30 days." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Gig Deletion: Upon gig deletion under a freelancer account, soft-deletion is performed i.e. data will be hidden from the public view but will be retained for legal and business purposes." />
        </ListItem>
      </List>

      <Typography paragraph>
        We may retain certain information longer if required by law, regulation, or legitimate business interests such as fraud prevention or dispute resolution.
      </Typography>

      <Typography id="section-8" variant="h5" gutterBottom mt={4}>
        8. International Data Transfers
      </Typography>
      <Typography variant="h6" gutterBottom>
        Cross-Border Transfers
      </Typography>
      <List>
        <ListItem>
          <ListItemText primary="Global Operations: Your data may be transferred to and processed in countries other than your country of residence." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Service Providers: We may share data with service providers located in different countries for platform operations." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Cloud Infrastructure: Data may be stored and processed using cloud services with global data centers." />
        </ListItem>
      </List>

      <Typography variant="h6" gutterBottom>
        Data Protection Safeguards
      </Typography>
      <List>
        <ListItem>
          <ListItemText primary="Adequacy Decisions: We rely on adequacy decisions by relevant data protection authorities where applicable." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Standard Contractual Clauses: Use EU-approved standard contractual clauses for transfers to third countries." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Binding Corporate Rules: Implement binding corporate rules for intra-group data transfers." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Certification Schemes: Participate in recognized certification schemes for data protection." />
        </ListItem>
      </List>

      <Typography paragraph>
        When we transfer your data internationally, we ensure appropriate safeguards are in place to protect your privacy rights and personal information in accordance with applicable data protection laws.
      </Typography>

      <Typography id="section-9" variant="h5" gutterBottom mt={4}>
        9. Children's Privacy
      </Typography>
      <Typography paragraph>
        Our services are not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately.
      </Typography>
      <Typography paragraph>
        For users between 13 and 18 years of age, we recommend parental guidance when using our platform. We may require parental consent for certain activities or data processing operations involving minors.
      </Typography>

      <Typography id="section-10" variant="h5" gutterBottom mt={4}>
        10. Third-Party Services
      </Typography>
      <Typography variant="h6" gutterBottom>
        Integrated Services
      </Typography>
      <List>
        <ListItem>
          <ListItemText primary="Payment Processors: Stripe, PayPal, and other payment services for transaction processing." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Cloud Services: AWS, Google Cloud, and other cloud providers for data storage and processing." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Communication Tools: Email services, SMS providers, and messaging platforms." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Analytics Services: Google Analytics, Mixpanel, and other analytics platforms." />
        </ListItem>
      </List>

      <Typography variant="h6" gutterBottom>
        External Links
      </Typography>
      <Typography paragraph>
        Our platform may contain links to third-party websites or services. We are not responsible for the privacy practices or content of these external sites. We encourage you to review the privacy policies of any third-party services you use.
      </Typography>

      <Typography id="section-11" variant="h5" gutterBottom mt={4}>
        11. Legal Basis for Processing
      </Typography>
      <Typography variant="h6" gutterBottom>
        Processing Grounds
      </Typography>
      <List>
        <ListItem>
          <ListItemText primary="Contract Performance: Processing necessary to provide our services and fulfill our contractual obligations." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Legitimate Interests: Processing for our legitimate business interests, such as platform improvement and fraud prevention." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Consent: Processing based on your explicit consent for specific purposes like marketing communications." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Legal Obligation: Processing required to comply with legal obligations and regulatory requirements." />
        </ListItem>
      </List>

      <Typography paragraph>
        We process your personal data based on one or more of these legal grounds, ensuring compliance with applicable data protection laws including GDPR, CCPA, and other relevant regulations.
      </Typography>

      <Typography id="section-12" variant="h5" gutterBottom mt={4}>
        12. Changes to This Privacy Policy
      </Typography>
      <Typography variant="h6" gutterBottom>
        Policy Updates
      </Typography>
      <List>
        <ListItem>
          <ListItemText primary="Regular Review: We regularly review and update this Privacy Policy to reflect changes in our practices and legal requirements." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Material Changes: For significant changes, we will provide notice through email, platform notifications, or prominent website notices." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Effective Date: Changes will be effective immediately upon posting unless otherwise specified." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Version History: We maintain a version history of our Privacy Policy for transparency and compliance purposes." />
        </ListItem>
      </List>

      <Typography paragraph>
        We encourage you to review this Privacy Policy periodically to stay informed about how we protect your information. Your continued use of our services after any changes constitutes acceptance of the updated policy.
      </Typography>

      <Typography id="section-13" variant="h5" gutterBottom mt={4}>
        13. Contact Us
      </Typography>
      <Typography variant="h6" gutterBottom>
        Privacy Inquiries
      </Typography>
      <Typography paragraph>
        If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
      </Typography>
      <List>
        <ListItem>
          <ListItemText primary="Email: info@geniegig.com" />
        </ListItem>
        <ListItem>
          <ListItemText primary="Subject Line: Privacy Policy Inquiry" />
        </ListItem>
        <ListItem>
          <ListItemText primary="Response Time: We will respond within 30 days of receiving your inquiry." />
        </ListItem>
      </List>

      <Typography variant="h6" gutterBottom>
        Data Protection Officer
      </Typography>
      <Typography paragraph>
        For users in the European Union, you may also contact our Data Protection Officer for matters related to GDPR compliance and data protection rights.
      </Typography>

      <Box mt={6} pt={4} borderTop={1} borderColor="grey.300">
        <Typography variant="body2" color="text.secondary">
          © {new Date().getFullYear()} GenieGig - A Product of PlatinumSpark
          Technologies Pvt. Ltd. All rights reserved.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Registered Office: E-322, Tagore Garden Extension, New Delhi, Delhi
          110027, India
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Email: info@geniegig.com | CIN: U62099DL2024PTC440048
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={2}>
          This Privacy Policy is effective as of {new Date().toLocaleDateString()} and was last updated on {new Date().toLocaleDateString()}.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          For questions about this Privacy Policy, please contact us at info@geniegig.com with the subject line "Privacy Policy Inquiry".
        </Typography>
      </Box>
    </Box>
  );
}


